import { type ReactNode, useId } from "react";
import { cn } from "../lib/utils.js";
import {
  type SettingsLook,
  SettingsLookContext,
} from "./settings-look-context.js";

/* The titled group of a settings page (PB-24). One of the three settings-page
   parts, with SectionHeader and SettingRow. Ported from the wireframe settings
   shell. */

interface SettingsGroupBaseProps {
  title: string;
  description?: string;
  /**
   * Keep the heading for assistive technology but take it off the screen.
   *
   * For a group whose name is ALREADY on screen immediately above it — the
   * selected tab, most often. GenAI's Connections tab said "Connections" three
   * times in a column: the tab, this eyebrow, and the first table header. The
   * heading still exists in the document outline, so the structure a screen
   * reader navigates by is unchanged.
   */
  titleHidden?: boolean;
  /** Small badge beside the title, as on SectionHeader. */
  badge?: ReactNode;
  /** The trailing action group, at the end of the heading row. */
  actionsSlot?: ReactNode;
  children: ReactNode;
}

type SettingsGroupVariantProps =
  | {
      /**
       * `default` is the uppercase eyebrow group with a hairline between
       * consecutive groups. `settings` is the Direction B group (PB-5134):
       * heading-3 title, body-small description, no separator, the section
       * named by its heading, and every SettingRow inside it on the Direction
       * B row.
       */
      variant?: "default";
      /**
       * Force the separator that a group normally gets only when it FOLLOWS
       * another group in the same parent. Set it where the rule cannot see the
       * sequence — a group rendered by a different component, so it is
       * `:first-of-type` in its own parent while sitting second on the page.
       */
      separated?: boolean;
    }
  | {
      variant: "settings";
      /** A settings group never draws a separator, so there is none to force. */
      separated?: never;
    };

export type SettingsGroupProps = SettingsGroupBaseProps &
  SettingsGroupVariantProps;

/* The title keeps its own classes in a heading row; the gap under it moves to
   the row, so the badge and actions centre on the title itself. */
const LOOK: Record<
  SettingsLook,
  { title: string; titleGap: string; description: string }
> = {
  default: {
    title:
      "text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted",
    titleGap: "mb-1",
    description: "text-xs text-pb-text-muted mb-3 leading-relaxed",
  },
  settings: {
    title: "text-heading-3 tracking-tight text-pb-text",
    titleGap: "",
    description: "mt-0.5 text-body-small text-pb-text-muted",
  },
};

/* `cond && <Badge />` passes `false`; that is no badge, not an empty row. */
const isSet = (node: ReactNode) => node != null && node !== false;

/** Titled section wrapper grouping related settings. */
export function SettingsGroup({
  title,
  description,
  titleHidden = false,
  separated = false,
  variant = "default",
  badge,
  actionsSlot,
  children,
}: SettingsGroupProps) {
  const titleId = useId();
  const look = LOOK[variant];
  const settings = variant === "settings";
  const inRow = isSet(badge) || isSet(actionsSlot);
  const heading = (
    <h2
      id={settings ? titleId : undefined}
      className={
        titleHidden ? "sr-only" : cn(look.title, !inRow && look.titleGap)
      }
    >
      {title}
    </h2>
  );
  return (
    /* One rhythm for consecutive default groups. `mb-8` alone left a Save bar
       and the next section's heading 32px apart with nothing between them, so
       a page read as one continuous run and the Save bar looked like the end
       of the page rather than the end of its own section. Every group after
       the first opens with a hairline and more air above it. */
    <section
      aria-labelledby={settings ? titleId : undefined}
      className={
        settings
          ? "mb-7.5"
          : cn(
              "mb-8",
              separated
                ? "mt-10 border-t border-pb-border/60 pt-8"
                : "[&:not(:first-of-type)]:mt-10 [&:not(:first-of-type)]:border-t [&:not(:first-of-type)]:border-pb-border/60 [&:not(:first-of-type)]:pt-8",
            )
      }
    >
      {inRow ? (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-x-4 gap-y-2",
            look.titleGap,
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {heading}
            {badge}
          </div>
          {actionsSlot}
        </div>
      ) : (
        heading
      )}
      {description ? <p className={look.description}>{description}</p> : null}
      <SettingsLookContext.Provider value={variant}>
        {settings ? (
          children
        ) : (
          <div className={description ? "" : "mt-3"}>{children}</div>
        )}
      </SettingsLookContext.Provider>
    </section>
  );
}
